/**
 * @file AccountAvatar Component
 * @description This component renders an avatar for the user account with associated menu and drawer for account management.
 */

import {
  AdminPanelSettingsOutlined,
  FactCheckOutlined,
  KeyboardArrowRightOutlined,
  LockOutlined,
  LogoutOutlined,
  SchoolOutlined,
  SettingsOutlined,
  SyncLockOutlined,
  ViewModuleOutlined,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import AvatarSVG from '../../assets/avatarSVG.svg';
import { getColor } from '../../assets/theme';

import useAppContext from '@hooks/useAppContext';
import FOCashBreakdownDrawer from '@modules/front-office/components/fo-cash-breakdown.drawer';
import { IconSunrise } from '@tabler/icons-react';
import { dateFormat } from '@utils/format';
import { usePOSMediumScreen, usePOSScreen } from '@utils/screen-size';
import moment from 'moment';
import useAuth from '../../hooks/useAuth';
import useDisclosure from '../../hooks/useDisclosure';
import useLayoutContext from '../../hooks/useLayoutContext';
import useUser from '../../hooks/useUser';
import { useAppSelector } from '../../store';
import { selectPropertyUserDetails } from '../../store/selector/property.selector';
import { guideTitleType } from '../../store/slices/guide.slice';
import { appMode } from '../../utils/url';
import DialogChangePassword from '../dialog/dialog-change-password';
import DialogChangeSecCode from '../dialog/dialog-change-sec-code';
import DialogManageSessions from '../dialog/dialog-manage-sessions';
import DialogRequestForTraining from '../dialog/dialog-request-for-training';
import DrawerWrapper from '../drawer/drawer-wrapper';
import { useCurrentModule } from '@hooks/useCurrentModule';
import { LiveClock } from './live-clock';
import { selectSalesDateByPath } from '@store/selector/app.selector';
import DialogSetupMFA from '@components/dialog/dialog-setup-mfa';

/**
 * @typedef {Object} AccountAvatarProps
 * @property {boolean} [noHover] - If true, disables hover effect on the avatar
 */

/**
 * AccountAvatar Component
 * @param {AccountAvatarProps} props - The component props
 * @returns {JSX.Element} The rendered component
 */
const AccountAvatar = ({ noHover }: { noHover?: boolean }) => {
  const { openSecCodeDialog, isLoggingOut } = useAppContext();
  const {
    danger,
    bgColor,
    primary,
    darkMode: isDark,
    secondary,
    offWhite,
  } = getColor();

  const [openOpt, setOpenOpt] = useState(false);

  /**
   * Toggles the options drawer
   */
  const handleClickOpt = () => {
    setOpenOpt((prev) => !prev);
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  /**
   * Handles click event on the avatar
   * @param {React.MouseEvent<HTMLElement>} event - The click event
   */
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Closes the account menu
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  // const dispatch = useDispatch();

  // const [checked, setChecked] = useState(isDark);
  // const [loggingOut, setLoggingOut] = useState(false);

  /**
   * Handles dark mode toggle
   * @param {ChangeEvent<HTMLInputElement>} event - The change event
   */
  // const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
  //   setChecked(event.target.checked);
  //   setTimeout(() => {
  //     dispatch(setDarkMode(event.target.checked));
  //   }, 250);
  // };

  const userDataCreds = useUser();

  const { logoutUser } = useAuth();

  useEffect(() => {
    !anchorEl &&
      setTimeout(() => {
        setOpenOpt(false);
      }, 100);
  }, [anchorEl]);

  // DIALOG COMPONENT STATE
  const {
    isOpen: isOpenRequestTraining,
    onOpen: onOpenRequestTraining,
    onClose: onCloseRequestTraining,
  } = useDisclosure();

  const {
    isOpen: isOpenChangePass,
    onOpen: onOpenChangePass,
    onClose: onCloseChangePass,
  } = useDisclosure();

  const {
    isOpen: isOpenChangeSecCode,
    onOpen: onOpenChangeSecCode,
    onClose: onCloseChangeSecCode,
  } = useDisclosure();

  const {
    isOpen: isOpenManageSession,
    onOpen: onOpenManageSession,
    onClose: onCloseManageSession,
  } = useDisclosure();

  const {
    isOpen: isOpenMFA,
    onOpen: onOpenMFA,
    onClose: onCloseMFA,
  } = useDisclosure();

  useEffect(() => {
    if (!openOpt) {
      onCloseChangePass();
    }
  }, [openOpt]);

  const currentModule = useCurrentModule();

  const { handleOpenModuleGuide, openComingSoon } = useLayoutContext();

  const userData = useAppSelector(selectPropertyUserDetails);

  // #region POS
  const cashBreakdownDisclosure = useDisclosure();
  const isPOSMediumScreen = usePOSMediumScreen();
  const [posStatus, setPosStatus] = useState<'end-shift' | 'declare-new-day'>(
    'end-shift',
  );

  const handlePOSMenu = async (status: 'end-shift' | 'declare-new-day') => {
    try {
      openSecCodeDialog(async (res) => {
        if (!res?.data.isValid) return;
        setPosStatus(status);
        cashBreakdownDisclosure.onOpen();
      }, 'force_end_shift');
    } catch (error) {
    } finally {
      handleClose();
    }
  };

  // #endregion

  // #region SALES DATE
  const pathname = window.location.pathname?.split('/')?.[1];
  const isPOS1024p = usePOSScreen();

  const currentSalesDateState = useAppSelector((state) =>
    selectSalesDateByPath(state, pathname),
  );

  // #endregion

  return (
    <>
      <Tooltip title='Account settings'>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            height: '100%',
            padding: '0 12px',
            ':hover': {
              bgcolor: noHover ? '' : '#414CC8',
            },
            cursor: isLoggingOut ? 'not-allowed' : 'pointer',
          }}
          className='header-icon'
          onClick={isLoggingOut ? () => {} : handleClick}
        >
          {isLoggingOut && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                right: 0,
                left: 0,
                zIndex: 2,
                opacity: 0.5,
                bgcolor: bgColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <Avatar
            id='account_avatar'
            sx={{ width: '32px', height: '32px' }}
            src={AvatarSVG}
          ></Avatar>
        </Box>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        id='account-menu'
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 1,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.025))',
            mt: 0,
            ul: {
              padding: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box
          sx={{
            padding: '12px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Avatar sx={{ width: '40px', height: '40px' }} src={AvatarSVG} />
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: '14px',
                letterSpacing: '.17px',
                fontWeight: '400',
              }}
            >
              {userData?.fullName}
            </Typography>
            {userData?.userRole && (
              <Typography
                sx={{
                  fontSize: '12px',
                  color: '#637381',
                  letterSpacing: '.4px',
                }}
              >
                {userData?.userRole}
              </Typography>
            )}
            <Typography
              sx={{ fontSize: '12px', color: '#637381', letterSpacing: '.4px' }}
            >
              {userData?.email}
            </Typography>
            <Typography
              sx={{ fontSize: '12px', color: '#637381', letterSpacing: '.4px' }}
            >
              Role Level: {userData?.accountLevel}
            </Typography>
            {/* <FormControlLabel
              control={
                <DarkModeSwitch checked={checked} onChange={handleChange} />
              }
              label=''
            /> */}
          </Box>
        </Box>

        <Stack>
          <List>
            {isPOS1024p &&
              currentSalesDateState &&
              (pathname === 'front-office' || pathname === 'point-of-sale') && (
                <Stack
                  gap={1}
                  direction='row'
                  sx={{
                    bgcolor: offWhite,
                    p: '8px 12px',
                    borderRadius: '360px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 2,
                  }}
                >
                  <Typography sx={{ color: primary, whiteSpace: 'nowrap' }}>
                    {moment(currentSalesDateState)
                      .startOf('day')
                      .format(dateFormat)}
                  </Typography>{' '}
                  {pathname === 'point-of-sale' && <LiveClock />}
                  <Chip
                    label={moment(currentSalesDateState)
                      .startOf('day')
                      .format('ddd')}
                    sx={{
                      bgcolor: primary,
                      color: '#FFF',
                      cursor: 'default',
                    }}
                  />
                </Stack>
              )}

            <ListItemButton
              onClick={handleClickOpt}
              disableRipple
              sx={{ color: primary, padding: '6px 24px' }}
            >
              <ListItemIcon sx={{ minWidth: '32px' }}>
                <SettingsOutlined sx={{ color: primary }} />
              </ListItemIcon>
              <ListItemText primary='Manage Account' />

              <KeyboardArrowRightOutlined
                sx={{
                  transform: openOpt ? 'rotate(-90deg)' : 'none',
                  transition: 'transform .2s ease',
                }}
              />
            </ListItemButton>

            {/* POS */}
            {isPOSMediumScreen && (
              <>
                <ListItemButton
                  disableRipple
                  sx={{ color: secondary, padding: '6px 24px' }}
                  onClick={() => handlePOSMenu('end-shift')}
                >
                  <ListItemIcon sx={{ minWidth: '32px' }}>
                    <IconSunrise style={{ color: secondary }} />
                  </ListItemIcon>
                  <ListItemText primary='End Shift' />
                </ListItemButton>

                <ListItemButton
                  disableRipple
                  sx={{ color: primary, padding: '6px 24px' }}
                  onClick={() => handlePOSMenu('declare-new-day')}
                >
                  <ListItemIcon sx={{ minWidth: '32px' }}>
                    <FactCheckOutlined style={{ color: primary }} />
                  </ListItemIcon>
                  <ListItemText primary='Declare New Day' />
                </ListItemButton>
              </>
            )}
            {/* END POS */}

            <ListItemButton
              disableRipple
              sx={{ color: primary, padding: '6px 24px' }}
              onClick={() => {
                if (appMode === 'production' || appMode === 'uat') {
                  return openComingSoon();
                }
                let guideName: guideTitleType;
                switch (currentModule) {
                  case 'FO':
                    guideName = 'frontOffice';
                    break;
                  case 'UM':
                    guideName = 'usersManagement';
                    break;
                  default:
                    guideName = null;
                    break;
                }

                handleOpenModuleGuide(guideName);
                handleClose();
              }}
            >
              <ListItemIcon sx={{ minWidth: '32px' }}>
                <ViewModuleOutlined sx={{ color: primary }} />
              </ListItemIcon>
              <ListItemText primary='Module Guide' />
            </ListItemButton>
            <ListItemButton
              disableRipple
              sx={{ color: primary, padding: '6px 24px' }}
              onClick={onOpenRequestTraining}
            >
              <ListItemIcon sx={{ minWidth: '32px' }}>
                <SchoolOutlined sx={{ color: primary }} />
              </ListItemIcon>
              <ListItemText primary='Request Training' />
            </ListItemButton>
            <ListItemButton
              disableRipple
              sx={{ color: primary, padding: '6px 24px' }}
              onClick={onOpenMFA}
            >
              <ListItemIcon sx={{ minWidth: '32px' }}>
                <AdminPanelSettingsOutlined sx={{ color: primary }} />
              </ListItemIcon>
              <ListItemText primary='Multi-factor Authentication' />
            </ListItemButton>
            <ListItemButton
              disableRipple
              sx={{ color: danger, padding: '6px 24px' }}
              onClick={async () => {
                try {
                  handleClose();

                  await logoutUser({
                    sessionID: userDataCreds?.userID || '',
                    token: userDataCreds?.token || '',
                  });
                } finally {
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: '32px' }}>
                <LogoutOutlined sx={{ color: danger }} />
              </ListItemIcon>
              <ListItemText primary='Logout' />
            </ListItemButton>
          </List>
        </Stack>
      </Menu>

      <DrawerWrapper
        {...{
          open: openOpt,
          onClose: () => setOpenOpt(false),
          withHeader: {
            title: 'Manage Account',
          },
          sx: { zIndex: 1300 },
        }}
      >
        <Stack sx={{ p: 3 }} gap={2}>
          <Button
            variant='outlined'
            startIcon={<LockOutlined />}
            sx={{
              justifyContent: 'flex-start',
              borderRadius: '8px',
              p: '16px 24px',
              gap: '10px',
              color: isDark ? '#FFF' : '',
            }}
            onClick={onOpenChangePass}
          >
            Change Password
          </Button>
          <Button
            variant='outlined'
            startIcon={<SyncLockOutlined />}
            sx={{
              justifyContent: 'flex-start',
              borderRadius: '8px',
              p: '16px 24px',
              gap: '10px',
              color: isDark ? '#FFF' : '',
            }}
            onClick={onOpenChangeSecCode}
          >
            Change Security Code
          </Button>
          <Button
            variant='outlined'
            startIcon={<LogoutOutlined />}
            sx={{
              justifyContent: 'flex-start',
              borderRadius: '8px',
              p: '16px 24px',
              gap: '10px',
              color: isDark ? '#FFF' : '',
            }}
            onClick={onOpenManageSession}
          >
            Manage Devices
          </Button>
          <Button
            variant='outlined'
            startIcon={<LogoutOutlined />}
            sx={{
              justifyContent: 'flex-start',
              borderRadius: '8px',
              p: '16px 24px',
              gap: '10px',
              color: isDark ? '#FFF' : '',
            }}
            onClick={onOpenMFA}
          >
            Enable Multi-Factor Authentication
          </Button>
        </Stack>
      </DrawerWrapper>

      {/* REQUEST FOR TRAINING */}
      <DialogRequestForTraining
        open={isOpenRequestTraining}
        onClose={onCloseRequestTraining}
      />

      {/* CHANGE PASS */}
      <DialogChangePassword
        open={isOpenChangePass}
        onClose={onCloseChangePass}
      />

      {/* CHANGE SEC CODE */}
      <DialogChangeSecCode
        open={isOpenChangeSecCode}
        onClose={onCloseChangeSecCode}
      />

      {/* MANAGE DEVICES */}
      <DialogManageSessions
        open={isOpenManageSession}
        onClose={onCloseManageSession}
      />

      {/* SETUP MFA */}
      <DialogSetupMFA
        key={open ? 'open' : 'closed'}
        open={isOpenMFA}
        onClose={onCloseMFA}
      />

      {isPOSMediumScreen && (
        <FOCashBreakdownDrawer
          {...{
            drawerDisclosure: cashBreakdownDisclosure,
            transactionType: posStatus,
          }}
        />
      )}
    </>
  );
};

export default AccountAvatar;
